import React from 'react';
import {
  NavLink,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { format, parseJSON } from "date-fns";
import titleize from "underscore.string/titleize";
import { FaExternalLinkAlt } from "react-icons/fa";
import {
  TeamMembers,
  Affiliations,
} from "../components/company";
import {
  AttributeValue,
  Layouts,
  Buttons,
  Loaders
} from "./../components";
import { 
  useCompany,
} from "./../queries/company";
import {
  getStateNameFromAbbr,
} from "./../config/constants";
import { useSessionContext } from "./../contexts/SessionContext";

export default function CompanyView() {
  const navigate = useNavigate();
  const location = useLocation();
  let { company_id } = useParams()
  const {data:company, isLoading } = useCompany({company_id})
  const { isPermitted, ACTIONS } = useSessionContext();

  const openEditorModal = React.useCallback(
    (row) => {
      navigate(
        "edit",
        {
          state: {
            backgroundLocation: location
          }
        }
      )
    },
    [navigate, location]
  )

  if (isLoading)
    return <Loaders.DashboardLoader />

  const issuerDetail = company?.issuer_detail || {};

  return (
    <>
      <Layouts.PageHeader
        left={<div onClick={() => window.open(`${process.env.REACT_APP_INVESTOR_APP_URL}/company/${company.slug}`, '_blank')}>{company.name} <FaExternalLinkAlt className="ms-2"/></div>}
      />
      <div className="overview-content">
        <div className="nav-tabs-container ps-3">
          <ul className="nav nav-tabs pe-3" role="tablist">
            <li className="nav-item">
              <NavLink to="company-overview" className="nav-link active" data-bs-toggle="tab" data-bs-target="#company-overview" role="tab">Overview</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="company-people" className="nav-link" data-bs-toggle="tab" data-bs-target="#company-people" role="tab">People</NavLink>
            </li>
          </ul>
        </div>
        <div className="tab-content px-3">
          <div className="tab-pane fade show active" id="company-overview" role="tabpanel">
            <Layouts.OverviewSubheader
              left='Overview'
              right={ 
                isPermitted(ACTIONS.WRITE_UPDATE_COMPANY, company.id) ? <Buttons.TextButton title='Edit' onClick={openEditorModal}/> : null
              }
              tooltipDesc="All public information related to your company. This info will show on your company profile and be used for your offer and pre-launch card. This is editable by Issuers. Company information is viewable by investors." 
            />
            <Layouts.TwoColumnLayout
              left={
                <>
                  <AttributeValue name='Company name' value={titleize(company.name)} />
                  <AttributeValue name='Legal name' value={titleize(company.legal_name)} />
                  <AttributeValue name='Website' value={company.url} isExternalLink={true}/>
                  <AttributeValue name='Company type' value={company.private ? 'Public' : 'Private'} />
                  <AttributeValue name='Sector' value={[company.parent_sector_name, company.sector_name].filter(Boolean).join(' - ')} />
                  <AttributeValue name='Last funding stage' value={company.last_funding_type} />
                </>
              }
              right={
                <>
                  <AttributeValue name='City' value={titleize(company.city)} />
                  {/* <AttributeValue name='State' value={titleize(company.state)} /> */}
                  <AttributeValue name='Country' value={company.country} />
                </>
              }
            />

            <Layouts.TwoColumnLayout
              left={
                <>
                  <AttributeValue name='Created on' value={format(parseJSON(company.created_at), 'MMM dd, yyyy')} />
                </>
              }
              right={
                <>
                  <AttributeValue name='Last updated on' value={format(parseJSON(company.updated_at), 'MMM dd, yyyy')} />
                </>
              }
            />

            <Layouts.OverviewSubheader left='Issuer Profile' tooltipDesc="Legal information about your company used for fundraising purposes. Your Issuer Profile is added by Spaced Ventures during the offer onboarding process." />
            <Layouts.TwoColumnLayout
              left={
                <>
                  <AttributeValue name='Company legal name' value={titleize(issuerDetail.name)} />
                  <AttributeValue name='Company email' value={issuerDetail.email} />
                  <AttributeValue name='Company phone number' value={issuerDetail.phone} />
                  <AttributeValue name='State formed in' value={getStateNameFromAbbr(issuerDetail.region_formed_in)} />
                  <AttributeValue name='Tax ID number' value={issuerDetail.tax_id_number} />
                  <AttributeValue name='Contact name' value={titleize(issuerDetail.contact_name)} />
                </>
              }
              right={
                <>
                  <AttributeValue name='Address' value={titleize(issuerDetail.street_address_1)} />
                  <AttributeValue name='City' value={titleize(issuerDetail.city)} />
                  <AttributeValue name='State' value={getStateNameFromAbbr(issuerDetail.region)} />
                  <AttributeValue name='Country' value={issuerDetail.country} />
                  <AttributeValue name='Zip code' value={issuerDetail.postal_code} />
                </>
              }
            />
          </div>
          <div className="tab-pane" id="company-people" role="tabpanel">
              <TeamMembers.List companyId={company.slug} />
              <Affiliations.List companyId={company.slug} />
          </div>
        </div>
    </div>
  </>
  )
}
